import { cloneDeep } from 'lodash';
import { Store } from 'redux';
import { createApplication } from '../Application/applicationFactory';
import { SubscriptionType } from '../Display/IConfig';
import { createAbsentResponsibilities } from '../Feature/Responsibilities';
import Responsibility from '../Feature/Responsibility';
import { IBundledApplet } from '../Front/Applet/BundledApplet/bundledAppletActions';
import { ManagementCacheDriver, normalizeManagementCacheDriver } from '../NativeDevice/Default/combinedDriver';
import createPropertyStorage from '../Property/propertyStorage';
import { initializeTelemetryStackStorage } from './Device/Telemetry/telemetryStackStorage';
import IBundledServlet from './Servlet/IBundledServlet';
import { IManagementState, managementReducer } from './managementReducers';
import { managementSaga } from './managementSagas';
import { startBackupRestartChecking } from '../BackupRestart/backupRestart';
import { createGetConfigUrl } from '../Device/Configuration/deviceConfigurationHelper';

export interface IManagementOptions {
	configUrls: string[];
	platformUri: string;
	staticBaseUrl: string;
	uploadBaseUrl: string;
	weinreServerUrl: string;
	extendedManagementUrl: string | null;
	checkInterval: number;
	publicKey: string;
	sessionIdKey: string;
	nativeDriver: ManagementCacheDriver;
	frontDisplayVersion: string;
	applicationVersion: string;
	subscriptionType: SubscriptionType;
	bundledApplet: null | IBundledApplet;
	bundledServlet: IBundledServlet | null;
	autoVerification: { organizationUid: string; deviceName?: string } | undefined;
	excludedResponsibilities: Responsibility[];
}

export default async (options: IManagementOptions) => {
	const { managementDriver, cacheDriver } = normalizeManagementCacheDriver(options.nativeDriver);

	const responsibilities = createAbsentResponsibilities(...options.excludedResponsibilities);
	const getConfigUrl = createGetConfigUrl(managementDriver, { configUrls: options.configUrls });
	if (responsibilities.has(Responsibility.POWER)) {
		startBackupRestartChecking(managementDriver, getConfigUrl);
	}

	const propertyStorage = createPropertyStorage(() => cacheDriver);

	// Initialize telemetry stack storage with PropertyStorage
	initializeTelemetryStackStorage(propertyStorage);

	const subscriptionType = options.subscriptionType;
	const { excludedResponsibilities: _, ...newOptions } = options;
	const {
		configUrls,
		platformUri,
		staticBaseUrl,
		uploadBaseUrl,
		checkInterval,
		weinreServerUrl: weinreUri,
		extendedManagementUrl,
		...sagaOptions
	} = {
		responsibilities,
		...newOptions,
	};
	const config = {
		configUrls,
		platformUri,
		staticBaseUrl,
		uploadBaseUrl,
		weinreUri,
		extendedManagementUrl,
		subscriptionType,
		checkInterval,
	};
	let lastSagaError: Error | undefined;
	const application = await createApplication(
		managementReducer,
		(store: Store<IManagementState>) => managementSaga(config, sagaOptions, store.getState, propertyStorage, store.dispatch, getConfigUrl),
		() => managementDriver,
		(e: Error) => {
			lastSagaError = cloneDeep(e);
			console.warn('Sagas broken', e);
		},
	);

	if (typeof window !== 'undefined') {
		(window as any).__management = {
			...options,
			store: application.store,
			storeEmitter: application.storeEmitter,
			stop: application.stop,
			lastSagaError,
		};
	}

	return { stop };
};
